import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';

import { styles } from './styles.ts';
import { Header } from './components/Header.tsx';
import { ConfigForm } from './components/ConfigForm.tsx';
import { RoomPreview } from './components/RoomPreview.tsx';
import { PromptSection } from './components/PromptSection.tsx';
import { LogsAndCode } from './components/LogsAndCode.tsx';
import { useRoomLayout } from './hooks/useRoomLayout.ts';

import { RoomArchitectChat, SkillsManagerUI, useSkillsManager } from '../../chatbot/ui.ts';
import { ZoningSection } from '../../zoning/ui.ts';

// ----------------------------- Main App Component ---------------------------

export function App() {
  const [activeTab, setActiveTab] = useState<'editor' | 'skills'>('editor');

  const {
    roomData,
    questionnaire,
    prompt,
    model,
    aiMessage,
    backendUrl,
    setBackendUrl,
    status,
    errorMessage,
    logs,
    generatedScript,
    validationResult,
    geminiCalls,
    searchLogs,
    isValidating,
    dbRooms,
    selectedRoomId,
    backendHealthy,
    historyIndex,
    history,
    // Zoning states & methods
    zones,
    isZoning,
    zoningPrompt,
    zoningModel,
    zoningExplanation,
    zoningErrorMessage,
    showZonesInPreview,
    focusedZoneId,
    handleGenerateZones,
    handleClearZones,
    handleZoningPromptChange,
    handleZoningModelChange,
    handleToggleShowZones,
    handleFocusZone,
    // Other handlers
    handleUndo,
    handleRedo,
    handleRoomSelect,
    handleSaveRoom,
    handleFieldChange,
    handleQuestionnaireChange,
    handlePromptChange,
    handleModelChange,
    handleRoomDataUpload,
    handleSolveError,
    handleSolveAllErrors,
    handleUpdateLayout
  } = useRoomLayout();

  const { skills } = useSkillsManager(backendUrl);

  const handleApplyPromptToOrchestrator = (promptText: string) => {
    handlePromptChange(promptText);
    const el = document.getElementById('design-prompt-textarea');
    if (el) {
      el.focus();
    }
  };

  const handleRunOrchestrator = (promptText: string) => {
    handlePromptChange(promptText);
    setTimeout(() => {
      handleUpdateLayout();
    }, 100);
  };

  return (
    <div style={styles.container}>
      <Header 
        backendHealthy={backendHealthy} 
        backendUrl={backendUrl} 
        setBackendUrl={setBackendUrl}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <main style={{
        ...styles.mainGrid,
        display: activeTab === 'editor' ? 'flex' : 'none'
      }}>
        {/* Step 1: Configuration Form */}
        <ConfigForm
          roomData={roomData}
          questionnaire={questionnaire}
          handleFieldChange={handleFieldChange}
          handleQuestionnaireChange={handleQuestionnaireChange}
          dbRooms={dbRooms}
          selectedRoomId={selectedRoomId}
          handleRoomSelect={handleRoomSelect}
          onRoomDataChange={handleRoomDataUpload}
        />

        {/* Step 2: Room Interactive Preview & Verification Accordions (with intelligent zones) */}
        <RoomPreview 
          roomData={roomData} 
          validationResult={validationResult}
          isValidating={isValidating}
          onSaveRoom={handleSaveRoom}
          onSolveError={handleSolveError}
          onSolveAllErrors={handleSolveAllErrors}
          status={status}
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={historyIndex > 0}
          canRedo={historyIndex < history.length - 1}
          zones={zones}
          showZonesInPreview={showZonesInPreview}
          focusedZoneId={focusedZoneId}
        />

        {/* Step 3: Intelligent Room Zoning Control Section */}
        <section style={{ gridColumn: 'span 12' }}>
          <ZoningSection
            prompt={zoningPrompt}
            onPromptChange={handleZoningPromptChange}
            model={zoningModel}
            onModelChange={handleZoningModelChange}
            onGenerateZones={handleGenerateZones}
            onClearZones={handleClearZones}
            isZoning={isZoning}
            zones={zones}
            aiExplanation={zoningExplanation}
            errorMessage={zoningErrorMessage}
            backendUrl={backendUrl}
            showZonesInPreview={showZonesInPreview}
            onToggleShowZones={handleToggleShowZones}
            onFocusZone={handleFocusZone}
            focusedZoneId={focusedZoneId}
          />
        </section>

        {/* Step 4: Conversational AI Room Architect Chat */}
        <section style={{ gridColumn: 'span 12' }}>
          <RoomArchitectChat
            backendUrl={backendUrl}
            roomData={roomData}
            skills={skills}
            onApplyPromptToOrchestrator={handleApplyPromptToOrchestrator}
            onRunOrchestrator={handleRunOrchestrator}
            onOpenSkillsLibrary={() => setActiveTab('skills')}
          />
        </section>

        {/* Step 5: Prompt Instructions Section (Orchestrator Textarea) */}
        <PromptSection
          model={model}
          prompt={prompt}
          status={status}
          errorMessage={errorMessage}
          aiMessage={aiMessage}
          onModelChange={handleModelChange}
          onPromptChange={handlePromptChange}
          onUpdateLayout={handleUpdateLayout}
        />

        {/* Step 5: Pipeline event logs, math code, and Gemini request inspector */}
        <LogsAndCode 
          logs={logs} 
          generatedScript={generatedScript} 
          geminiCalls={geminiCalls} 
          searchLogs={searchLogs}
        />
      </main>

      <main style={{ 
        padding: '24px', 
        maxWidth: '1400px', 
        margin: '0 auto',
        display: activeTab === 'skills' ? 'block' : 'none'
      }}>
        <SkillsManagerUI backendUrl={backendUrl} />
      </main>
    </div>
  );
}


const root = createRoot(document.getElementById('root')!);
root.render(<App />);
